import background from "@assets/footer.webp";
import Discord from "@core-components/header/Discord";
import Logo from "@core-components/header/Logo";
import Icon from "@core-components/icon/Icon";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import EPublicRoutes, { EExternalRoutes } from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import {
  ActionIcon,
  BackgroundImage,
  Divider,
  Flex,
  Group,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { AppNotification } from "@notifications/Notification";
import { Link } from "react-router";

const Footer: React.FC = () => {
  const { t } = useAppTranslation("components.layout.footer");
  const { locationUrlBuilder } = useAppLanguage();

  const sectionHeader = (label: string) => (
    <SSHeadline
      props={{
        stack: {
          align: "left",
        },
        title: {
          fz: 22,
        },
      }}
    >
      <SSWhiteText className="!text-primary-lighter">{label}</SSWhiteText>
    </SSHeadline>
  );

  const link = (label: string, to: string, inNewTab = false) => (
    <Text
      className="font-inter font-medium"
      component={Link}
      to={to}
      {...(inNewTab ? { target: "_blank", rel: "noreferrer noopener" } : "")}
    >
      {label}
    </Text>
  );

  const year = new Date().getFullYear();

  return (
    <BackgroundImage
      src={background}
      title={t("bgImageAlt")}
      className="relative z-10"
    >
      <div className="px-[15px] md:px-[44px]">
        <Space h={64} />
        <Flex
          align="top"
          className="flex-col justify-start xs:justify-around sm:flex-row"
        >
          <Stack className="pb-8 text-primary-white md:pb-0">
            {sectionHeader(t("navigation"))}

            {link(
              t("smurfAccounts"),
              locationUrlBuilder(EPublicRoutes.SMURFS_NORMAL),
            )}
            {link(
              t("handlevelAccounts"),
              locationUrlBuilder(EPublicRoutes.SMURFS_HANDLEVEL),
            )}
            {link(
              t("skinAccounts"),
              locationUrlBuilder(EPublicRoutes.SMURFS_SKIN),
            )}
            {link(
              t("rankedAccounts"),
              locationUrlBuilder(EPublicRoutes.SMURFS_RANKED),
            )}
            {link(
              t("valorantAccounts"),
              locationUrlBuilder(EPublicRoutes.VALORANT),
            )}
            {link(t("blog"), locationUrlBuilder(EPublicRoutes.BLOGS))}
          </Stack>

          <Stack className="pb-8 text-primary-white md:pb-0">
            {sectionHeader(t("others"))}

            <Discord />
            {link(t("faqSupport"), locationUrlBuilder(EPublicRoutes.FAQ))}
            {link(t("tos"), locationUrlBuilder(EPublicRoutes.CGV))}
            {link(t("privacyPolicy"), locationUrlBuilder(EPublicRoutes.PC))}
          </Stack>
          <Stack className="text-primary-white">
            {sectionHeader(t("followUs"))}

            <Group>
              <ActionIcon
                component={Link}
                rel="noreferrer noopener"
                target="_blank"
                to={locationUrlBuilder(EExternalRoutes.DISCORD)}
                variant="transparent"
              >
                <Icon.Discord size={30} />
              </ActionIcon>
              <ActionIcon
                component={Link}
                rel="noreferrer noopener"
                target="_blank"
                to={locationUrlBuilder(EExternalRoutes.TWITTER)}
                variant="transparent"
              >
                <Icon.Twitter size={35} />
              </ActionIcon>
              <ActionIcon
                variant="transparent"
                onClick={() =>
                  AppNotification.default({
                    title: t("comingSoon"),
                    message: "",
                  })
                }
              >
                <Icon.Youtube size={30} />
              </ActionIcon>
            </Group>
            <Logo h={50} />
          </Stack>
        </Flex>
        <Space h={32} />

        <Divider className="border-t-primary-light" />
        <Text
          className="font-inter font-normal text-primary-dark-text text-sm"
          py={32}
          ta={"center"}
        >
          {t("disclaimer")}
        </Text>
        <Group justify="space-between" py={32}>
          <Text className="font-inter font-normal text-primary-lighter text-sm">
            © {year} SECRETSMURFS
          </Text>
          <Group gap={25}>
            <Icon.MasterCard className="grayscale" size={20} />
            <Icon.Visa className="grayscale" size={20} />
            <Icon.Paypal className="grayscale" size={20} />
            <Icon.Bitcoin className="grayscale" size={20} />
          </Group>
        </Group>
      </div>
    </BackgroundImage>
  );
};

export default Footer;
